import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PatientSummary } from '@/api/patients/patientsApi.types';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { PatientFormModal } from '@/components/practitioner/PatientForm/PatientFormModal';
import { RelatedRecordsSection } from '@/components/practitioner/shared/RelatedRecordsSection';
import { useClientPatientsQuery } from '@/hooks/useClientPatients';
import { formatPatientAge } from '@/utils/patientAge';

interface PetsPanelProps {
  clientId: string;
}

export function PetsPanel({ clientId }: PetsPanelProps) {
  const { t } = useTranslation(['clients', 'common']);
  const navigate = useNavigate();
  const patientsQuery = useClientPatientsQuery(clientId);
  const [isAdding, setIsAdding] = useState(false);
  const pets = patientsQuery.data === undefined ? [] : patientsQuery.data;

  return (
    <>
      <RelatedRecordsSection
        title={t('clients:pets.title')}
        count={pets.length}
        isLoading={patientsQuery.isLoading}
        isError={patientsQuery.isError}
        onRetry={function retry() {
          patientsQuery.refetch();
        }}
        actions={
          <Button
            size="small"
            onClick={function open() {
              setIsAdding(true);
            }}
          >
            <Icon name="Plus" /> {t('clients:pets.add')}
          </Button>
        }
      >
        {pets.length === 0 ? (
          <p className="text-sm text-zoomuted py-2">{t('clients:pets.empty')}</p>
        ) : (
          <div className="space-y-2.5">
            {pets.map(function renderPet(pet) {
              return (
                <PetRow
                  key={pet.id}
                  pet={pet}
                  onOpen={function open() {
                    navigate('/workspace/patients/' + pet.id);
                  }}
                />
              );
            })}
          </div>
        )}
      </RelatedRecordsSection>

      {isAdding === true ? (
        <PatientFormModal
          clientId={clientId}
          patient={null}
          onClose={function close() {
            setIsAdding(false);
          }}
        />
      ) : null}
    </>
  );
}

function PetRow({ pet, onOpen }: { pet: PatientSummary; onOpen: () => void }) {
  const { t } = useTranslation('common');
  const ageLabel = formatPatientAge(pet.dateOfBirth, t);
  const meta = [pet.species, pet.breed]
    .filter(function keep(part) {
      return part !== null && part.trim().length > 0;
    })
    .join(' · ');

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left flex items-center gap-3 p-3 border border-zooborder rounded-xl hover:bg-zoovpalelavender hover:bg-opacity-30 transition-colors"
    >
      <span className="w-10 h-10 rounded-xl bg-zoovpalelavender flex items-center justify-center overflow-hidden shrink-0">
        {pet.photoUrl === null ? (
          <Icon name="PawPrint" className="text-zooprimary" />
        ) : (
          <img src={pet.photoUrl} alt={pet.name} className="w-full h-full object-cover" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-zooindigo truncate">{pet.name}</div>
        <div className="text-xs text-zoomuted truncate">
          {meta}
          {ageLabel === null ? '' : ' · ' + ageLabel}
        </div>
      </div>
      {pet.warnings.length === 0 ? null : (
        <Icon name="AlertCircle" className="text-zoowarningtext shrink-0" />
      )}
      <Icon name="ChevronRight" className="text-zoomuted shrink-0" />
    </button>
  );
}
